// Lead entry selection helpers.
// Rule: the "main" entry on a multi-sender lead is the one with the LOWEST
// askingPrice. Ties are broken by the earliest submittedAt.

import { getSenderDomainFavicon } from '../lib/sourceFaviconUtils';

const entryTs = (e) =>
  e?.submittedAt?._seconds ??
  e?.submittedAt?.seconds ??
  (typeof e?.submittedAt === 'number' ? e.submittedAt : 0) ??
  0;

const entryPrice = (e) =>
  typeof e?.askingPrice === 'number' && e.askingPrice > 0
    ? e.askingPrice
    : Infinity;

export function pickMainEntry(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return [...entries].sort((a, b) => {
    const dp = entryPrice(a) - entryPrice(b);
    if (dp !== 0) return dp;
    return entryTs(a) - entryTs(b);
  })[0];
}

// The MOST RECENT priced entry — mirrors functions/utils/entryHelper.js
// pickLatestEntry. Used wherever the latest submission is the seller's own
// current price (on-market listings, direct-to-seller, the X poller), up OR
// down — unlike pickMainEntry's lowest-wins anchor for off-market wholesale
// re-blasts. Prefers priced entries so a bare re-blast can't blank the
// headline; timestamp ties break to the higher price.
export function pickLatestEntry(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const priced = entries.filter(
    (e) => typeof e?.askingPrice === 'number' && e.askingPrice > 0
  );
  const pool = priced.length ? priced : entries;
  return [...pool].sort((a, b) => {
    const dt = entryTs(b) - entryTs(a);
    if (dt !== 0) return dt;
    return entryPrice(b) - entryPrice(a);
  })[0];
}

// THE REAL ASK — the price the source stated, never anything an operator typed.
//
// Reads `data.ingestedAskingPrice`, which only the ingestion pipeline writes
// (functions/propertyIngestionV2/batchedFirestore.js: saveLeadBatched on first
// ingest, addEntryToLeadBatched on a genuine re-ingested price change). No
// frontend path writes it, so a calculator edit cannot move it — which is the
// whole point. `currentAskingPrice` deliberately is NOT consulted: it doubles
// as the calculator's editable purchase price and has four writers outside
// ingestion — three on the frontend (this page's blur, DealDetail's auto-save,
// LeadsTable's inline edit) plus the rematchLeadBuyers callable.
//
// Fallback for docs that predate the field (until
// functions/scripts/backfillIngestedAskingPrice.js has run): re-derive it from
// `data.entries[]`, which is also ingestion-only, using the same branch the
// backend resolves the headline price with (below).
//
// Returns null rather than guessing: a lead with no ingested price (e.g. an
// auction, where the opening bid is deliberately not imported) has no real ask
// to show, and showing the edited figure there is the bug this replaces.
export function resolveIngestedAskingPrice(lead) {
  if (!lead) return null;
  const stamped = lead.ingestedAskingPrice;
  if (typeof stamped === 'number' && Number.isFinite(stamped) && stamped > 0) {
    return stamped;
  }
  const entries = Array.isArray(lead.entries) ? lead.entries : [];
  if (entries.length === 0) return null;
  // Mirrors addEntryToLeadBatched's branch, or a multi-entry lead resolves to a
  // different price here than ingestion would have stamped:
  //   • MLS / non-property-lead types (DTS…) and the X poller (an authoritative
  //     single seller, so re-posts are that seller's own price updates)
  //     → most recent wins, up or down.
  //   • property leads (competing wholesaler re-blasts) → on-market takes the
  //     latest price (the seller most recently set it); off-market/unknown
  //     keeps the lowest-price negotiation anchor.
  const leadType = lead.leadType || 'property_lead';
  // normalizeChannel collapses originSource 'x_spencer_public' → source 'x'.
  const authoritativeSeller = lead.source === 'x' || lead.originSource === 'x_spencer_public';
  let picked;
  if (leadType !== 'property_lead' || authoritativeSeller) {
    picked = pickLatestEntry(entries);
  } else {
    // Trimmed + lowercased to match resolveMarketStatus (assumptionScenario.js),
    // whose ON_MARKET_STATUSES set is exactly {for_sale, pending}.
    const onMarket = Object.values(lead.listingStatus || {}).some((s) => {
      const v = typeof s === 'string' ? s.trim().toLowerCase() : null;
      return v === 'for_sale' || v === 'pending';
    });
    picked = onMarket ? pickLatestEntry(entries) : pickMainEntry(entries);
  }
  const price = picked?.askingPrice;
  return typeof price === 'number' && Number.isFinite(price) && price > 0
    ? price
    : null;
}

export function hasLowerPricedSibling(entries) {
  if (!Array.isArray(entries) || entries.length < 2) return false;
  const main = pickMainEntry(entries);
  const mainPrice = entryPrice(main);
  if (mainPrice === Infinity) return false;
  return entries.some(
    (e) =>
      e !== main &&
      typeof e?.askingPrice === 'number' &&
      e.askingPrice > mainPrice
  );
}

// Single source of truth for the headline label on a sender card / row.
// Inbound emails (Unipile) often arrive with name=null when the From header
// had no display name, so we fall back through company → email → phone before
// surrendering to the placeholder.
export function getSenderDisplay(sender, placeholder = '—') {
  if (!sender) return placeholder;
  return sender.name || sender.company || sender.email || sender.phone || placeholder;
}

export function getSenderInitials(sender, placeholder = '—') {
  if (!sender) return placeholder;
  if (sender.name) {
    return sender.name
      .split(' ')
      .filter(Boolean)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }
  const fallback = sender.company || sender.email || sender.phone;
  if (!fallback) return placeholder;
  return fallback.trim()[0]?.toUpperCase() || placeholder;
}

// Resolve a sender company logo URL for the "Sent by" card / Main contact cell.
// Priority: (B) a logo image the vision classifier picked out of the deal's
// photos → (A) a favicon derived from the sender's corporate email domain →
// null (caller renders initials). Suppressed for forwarded leads, where the
// "sender" is a no-reply relay and the domain would mislead.
export function resolveSenderLogo(lead, mainSender) {
  if (!lead || lead.senderMissing === true) return null;
  if (typeof lead.senderLogoImageUrl === 'string' && lead.senderLogoImageUrl) {
    return lead.senderLogoImageUrl;
  }
  return getSenderDomainFavicon(mainSender?.email);
}
